import type { TFunction } from "i18next";

// Δ column naming, in a leaf module: `deltaColumns.tsx` reaches React and whole
// pages, and importing it to spell an id closes a cycle that leaves these in TDZ.

/** Where a grain keeps its Compare-to copy, e.g. `contracts.compareTo`. */
export type CompareTranslationKey = `${string}.compareTo`;

/** The suffixes that turn a metric column id into its Δ and Impact column ids. */
export const DELTA_COLUMN_SUFFIX = "Delta";
export const DELTA_IMPACT_COLUMN_SUFFIX = "DeltaImpact";

export const deltaColumnId = (metricKey: string): string =>
  `${metricKey}${DELTA_COLUMN_SUFFIX}`;

export const deltaImpactColumnId = (metricKey: string): string =>
  `${metricKey}${DELTA_IMPACT_COLUMN_SUFFIX}`;

/** `Δ ARR (YTD)` — the metric's own label with the selected timeframe appended. */
export const deltaHeader = (metricLabel: string, timeframe: string): string =>
  `Δ ${metricLabel} (${timeframe})`;

export const deltaImpactHeader = (
  metricLabel: string,
  timeframe: string,
  t: TFunction,
  translationKey: CompareTranslationKey
): string => `Δ ${metricLabel} ${t(`${translationKey}.impact`)} (${timeframe})`;

/**
 * The sign to put in front of a movement's magnitude, as that magnitude will be
 * displayed.
 *
 * Shared because the rule is subtler than it looks and has regressed once in
 * production: at whole-currency precision a movement of a few cents displays as
 * "0", and prepending a sign to that renders the nonsense "+$0" or "-$0" (seen
 * live on two contracts). So the question is whether the figure survives
 * rounding *to the precision the caller renders at*, not whether the raw number
 * is non-zero.
 *
 * So the caller states the `fractionDigits` it renders at and this decides the
 * rest. It deliberately does **not** take the formatted string, and it
 * deliberately does not take a pre-rounded number either:
 *
 * - Testing the string for a non-zero digit is the obvious implementation and
 *   is wrong. `Intl.NumberFormat` renders in the viewer's own locale, and an
 *   Arabic or Persian one emits Arabic-Indic digits ("٤٢٦٬٧٨٢"), where an ASCII
 *   `[1-9]` test finds nothing and reports a real movement as having rounded to
 *   zero — dropping the sign, and with it the direction colour on the tile.
 * - Taking a magnitude the caller rounded itself moves the rule back out to the
 *   call sites, where each has to round at the same precision it renders at and
 *   nothing checks that it did. Owning the rounding here keeps one place
 *   deciding whether a movement survives its own display.
 *
 * Both current callers render whole currency units and pass `0`. It has no
 * default: a caller that has not thought about its own precision is exactly the
 * one this argument exists to catch.
 */
export const deltaSign = (
  delta: number,
  fractionDigits: number
): "" | "+" | "-" => {
  // A non-finite movement has no direction worth asserting, and `Infinity` is
  // reachable: a number literal too large for a double parses out of JSON as
  // `Infinity`, so the wire can deliver one. Unsigned is also what the earlier
  // string-based implementation did here, by accident rather than intent —
  // neither "∞" nor "NaN" carries a digit its test looked for.
  if (!Number.isFinite(delta)) return "";
  const scale = 10 ** fractionDigits;
  const survivesRounding = Math.round(Math.abs(delta) * scale) !== 0;
  if (!survivesRounding || delta === 0) return "";
  return delta > 0 ? "+" : "-";
};
